/**
 * Proactive per-turn token cost guard (Hermes-parity superiority item #34).
 *
 * Hermes (and pi today) only react to context growth by compressing AFTER it's expensive. This estimates
 * the dollar cost of the NEXT LLM call BEFORE it is submitted, so the agent can warn the user or
 * automatically reduce reasoning effort before a runaway billing spike — a proactive ceiling, not a
 * reactive cleanup. Pure functions: no I/O, fully testable.
 */

/** Per-million-token USD prices, as carried on `Model.cost`. */
export interface ModelTokenCost {
	input: number;
	output: number;
	cacheRead?: number;
	cacheWrite?: number;
}

/**
 * Estimate the USD cost of one turn: the whole current context is billed as input, plus up to
 * `maxOutputTokens` of output. `cachedInputTokens` (prefix-cache hits) are billed at the cheaper
 * cache-read rate. Fresh input is billed once at the higher of the uncached-input and cache-write
 * rates because a provider may select that prefix for a new cache write. This is an UPPER bound on
 * the turn (it also assumes the model emits its full output budget), which is what a spending ceiling
 * should bound against.
 */
export function estimateTurnCostUsd(args: {
	inputTokens: number;
	maxOutputTokens: number;
	cost: ModelTokenCost;
	cachedInputTokens?: number;
	longContextPricing?: { thresholdTokens: number; inputMultiplier: number; outputMultiplier: number };
}): number {
	const { inputTokens, maxOutputTokens, cost } = args;
	const cached = Math.max(0, Math.min(args.cachedInputTokens ?? 0, inputTokens));
	const freshInput = inputTokens - cached;
	const cacheReadRate = cost.cacheRead ?? cost.input;
	const freshInputRate = Math.max(cost.input, cost.cacheWrite ?? cost.input);
	const longContextPricing = args.longContextPricing;
	const useLongContextTier = longContextPricing && inputTokens > longContextPricing.thresholdTokens;
	const inputMultiplier = useLongContextTier ? longContextPricing.inputMultiplier : 1;
	const outputMultiplier = useLongContextTier ? longContextPricing.outputMultiplier : 1;
	const inputUsd = ((freshInput * freshInputRate + cached * cacheReadRate) * inputMultiplier) / 1_000_000;
	const outputUsd = (Math.max(0, maxOutputTokens) * cost.output * outputMultiplier) / 1_000_000;
	return inputUsd + outputUsd;
}

/** What to do when a turn's projected cost exceeds the threshold. */
export type CostGuardAction = "warn" | "downgrade";

export interface CostGuardSettings {
	/** Explicit opt-in. A stored threshold without this flag is dormant legacy configuration. */
	enabled: boolean;
	/** Per-turn projected USD threshold. Must also be explicitly enabled to activate the guard. */
	maxTurnUsd: number;
	/** Over the ceiling: `warn` (surface a notice) or `downgrade` (also reduce reasoning effort). */
	action: CostGuardAction;
}

export const DEFAULT_COST_GUARD_SETTINGS: CostGuardSettings = {
	enabled: false,
	maxTurnUsd: 0,
	action: "warn",
};

/** Threshold offered when the user enables an unconfigured guard from `/settings`. */
export const DEFAULT_ENABLED_COST_GUARD_MAX_TURN_USD = 0.5;

export interface CostGuardDecision {
	/** True only after explicit opt-in and when projected TOTAL (estUsd + backgroundUsd) exceeds the ceiling. */
	over: boolean;
	/** Projected USD cost of the next foreground call alone (unchanged meaning — what the footer's "/turn" label shows). */
	estUsd: number;
	/** Background/spawned-lane USD spend (research/worker/reflection/fitness) folded into this decision — the caller decides the window (e.g. since the current turn began). */
	backgroundUsd: number;
	/** `estUsd + backgroundUsd` — the value actually compared against `thresholdUsd`. */
	totalUsd: number;
	thresholdUsd: number;
	action: CostGuardAction;
}

/**
 * Decide whether the projected turn cost trips the guard. `enabled: true` is mandatory; a positive
 * legacy threshold by itself stays dormant.
 *
 * `cumulativeBackgroundUsd` (default 0) is spawned/background-lane spend (research, worker delegation,
 * reflection, model-fitness probes — see {@link SpawnedUsageTotals}) folded into the same ceiling as the
 * next foreground call. This function is window-agnostic — it just sums whatever the caller passes — but
 * the intended caller convention (agent-session.ts) is spend attributed to the CURRENT turn only (a
 * baseline snapshotted at the top of the turn, subtracted from the live cumulative total), not the
 * session's entire lifetime, so a turn that is cheap in the foreground but has quietly spent a lot in
 * background lanes THIS turn still trips the guard, without a prior turn's spend keeping it stuck over
 * forever (still warn-only by default). `cost-guard-controller.ts` owns that bracketing convention.
 */
export function evaluateCostGuard(
	estUsd: number,
	settings: CostGuardSettings,
	cumulativeBackgroundUsd = 0,
): CostGuardDecision {
	const enabled = settings.enabled && settings.maxTurnUsd > 0;
	const backgroundUsd = Math.max(0, cumulativeBackgroundUsd);
	const totalUsd = estUsd + backgroundUsd;
	return {
		over: enabled && totalUsd > settings.maxTurnUsd,
		estUsd,
		backgroundUsd,
		totalUsd,
		thresholdUsd: settings.maxTurnUsd,
		action: settings.action,
	};
}

/** Reasoning levels in descending cost order, used to pick the next-cheaper level on a downgrade. */
const REASONING_LADDER = ["ultra", "max", "xhigh", "high", "medium", "low", "minimal", "off"] as const;
export type ReasoningLevel = (typeof REASONING_LADDER)[number];
export type ReasoningEffortMap = Readonly<Partial<Record<ReasoningLevel, string | null>>>;

function effectiveReasoningEffort(level: ReasoningLevel, effortMap: ReasoningEffortMap | undefined): string | null {
	const mapped = effortMap?.[level];
	return mapped === undefined ? level : mapped;
}

/**
 * One supported step down the reasoning ladder (cost reduction) from `current`. When
 * `supportedLevels` is provided, unsupported intermediate levels are skipped instead of relying on a
 * provider clamp that may move back upward. Returns `current` unchanged when already at the model's
 * floor or unrecognized — the guard never raises effort, only lowers it.
 */
export function downgradeReasoning(
	current: string,
	supportedLevels?: readonly ReasoningLevel[],
	effortMap?: ReasoningEffortMap,
): ReasoningLevel | string {
	const i = REASONING_LADDER.indexOf(current as ReasoningLevel);
	if (i < 0) return current;
	if (!supportedLevels) {
		return REASONING_LADDER[Math.min(i + 1, REASONING_LADDER.length - 1)];
	}
	const supported = new Set<ReasoningLevel>(supportedLevels);
	const currentEffort = effectiveReasoningEffort(current as ReasoningLevel, effortMap);
	for (let nextIndex = i + 1; nextIndex < REASONING_LADDER.length; nextIndex++) {
		const candidate = REASONING_LADDER[nextIndex];
		if (supported.has(candidate) && effectiveReasoningEffort(candidate, effortMap) !== currentEffort)
			return candidate;
	}
	return current;
}
